import {
   EnumIntegrationConnectorDispatcher,
   EnumIntegrationConnectorType,
   UpdateByIdIntegrationConnectorCloudTasksProductionInput,
   UpdateByIdIntegrationConnectorEventbusProductionInput
} from "../../Queries";

export type FormEventBus = Partial<UpdateByIdIntegrationConnectorEventbusProductionInput>

export type FormWebhook = {
   url?: string
   headers?: string
}

export type FormCloudTasks = Partial<UpdateByIdIntegrationConnectorCloudTasksProductionInput>

type ConnectorCommunicationFormData = {
   type: EnumIntegrationConnectorType
   dispatcher: EnumIntegrationConnectorDispatcher
   eventbus?: {
      development?: FormEventBus
      production?: FormEventBus
      test?: FormEventBus
   }
   webhook?: {
      development?: FormWebhook
      production?: FormWebhook
      test?: FormWebhook
   }
   cloudTasks?: {
      development?: FormCloudTasks
      production?: FormCloudTasks
      test?: FormCloudTasks
   }
}

export const resolveConnectorDispatcher = <T extends string>(
   dispatcher: T | null | undefined,
   functions2Dispatcher: T
): T => dispatcher ?? functions2Dispatcher

const toWebhookHeadersObject = (headers?: string) => {
   if (!headers || !headers.trim()) {
      return undefined
   }
   try {
      const parsed = JSON.parse(headers)
      return typeof parsed === 'object' && parsed !== null ? parsed : undefined
   } catch (error) {
      return undefined
   }
}

const toWebhookEnv = (env?: FormWebhook) => {
   if (!env?.url) {
      return undefined
   }
   return {
      url: env.url,
      headers: toWebhookHeadersObject(env.headers)
   }
}

const toCloudTasksEnv = (env?: FormCloudTasks) => {
   if (!env || (!env.queue?.trim() && !env.targetUrl?.trim() && !env.audience?.trim())) {
      return undefined
   }

   return {
      queue: env.queue ?? '',
      targetUrl: env.targetUrl ?? '',
      audience: env.audience?.trim() || undefined
   }
}

export const serializeConnectorCommunication = (data: ConnectorCommunicationFormData) => {
   const eventbus = data.type === EnumIntegrationConnectorType.EVENTBUS && data.eventbus ? {
      production: {
         serviceBus: data.eventbus.production?.serviceBus ?? '',
         topic: data.eventbus.production?.topic ?? ''
      },
      development: {
         serviceBus: data.eventbus.development?.serviceBus ?? '',
         topic: data.eventbus.development?.topic ?? ''
      },
      test: {
         serviceBus: data.eventbus.test?.serviceBus ?? '',
         topic: data.eventbus.test?.topic ?? ''
      }
   } : null

   let webhook = null
   if (data.type === EnumIntegrationConnectorType.WEBHOOK && data.webhook) {
      const production = toWebhookEnv(data.webhook.production)
      const development = toWebhookEnv(data.webhook.development)
      const test = toWebhookEnv(data.webhook.test)
      if (production || development || test) {
         webhook = {
            production,
            development,
            test
         }
      }
   }

   let cloudTasks = null
   if (data.type === EnumIntegrationConnectorType.CLOUD_TASKS && data.cloudTasks) {
      const production = toCloudTasksEnv(data.cloudTasks.production)
      const development = toCloudTasksEnv(data.cloudTasks.development)
      const test = toCloudTasksEnv(data.cloudTasks.test)
      if (production || development || test) {
         cloudTasks = {
            production,
            development,
            test
         }
      }
   }

   return {dispatcher: data.dispatcher, eventbus, webhook, cloudTasks}
}
